import { useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { useCapabilityDetail } from '@/hooks/use-entity-detail';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { Card } from '@/components/ui/card';
import { Cpu, Zap } from 'lucide-react';

interface ProviderConfig {
    supportedModels?: string[];
    supported_models?: string[];
    models?: string[];
    features?: string[];
    vendor?: string;
    type_config?: ProviderConfig;
    [key: string]: unknown;
}

function asStringList(...candidates: unknown[]): string[] {
    for (const raw of candidates) {
        if (!Array.isArray(raw)) continue;
        const list = raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
        if (list.length > 0) return list;
    }
    return [];
}

/** Resolve provider type_config from capability detail (working revision preferred). */
function resolveProviderConfig(data: unknown): ProviderConfig {
    const d = data as {
        config?: ProviderConfig;
        type_config?: ProviderConfig;
        working_revision?: { type_config?: ProviderConfig };
    } | null | undefined;

    const raw =
        d?.working_revision?.type_config ??
        d?.type_config ??
        d?.config ??
        {};

    // Import artifact may nest type_config inside type_config.
    if (raw.type_config && typeof raw.type_config === 'object' && !Array.isArray(raw.type_config)) {
        const { type_config: nested, ...rest } = raw;
        return { ...rest, ...nested };
    }
    return raw;
}

function SupportedModelsTab({
    supportedModels,
    features,
}: {
    supportedModels: string[];
    features: string[];
}) {
    return (
        <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Supported Models</h3>
            {supportedModels.length > 0 ? (
                <div className="grid gap-2">
                    {supportedModels.map((model: string) => (
                        <div
                            key={model}
                            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
                        >
                            <Cpu size={16} className="text-slate-500" />
                            <span className="font-mono text-sm text-slate-700">{model}</span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-slate-500">
                    No supported models listed. Model information is provided by the
                    provider&apos;s <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">info()</code> method at runtime.
                </p>
            )}

            {features.length > 0 && (
                <div className="mt-6">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Features</h4>
                    <div className="flex flex-wrap gap-2">
                        {features.map((feature: string) => (
                            <span
                                key={feature}
                                className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700"
                            >
                                <Zap size={10} />
                                {feature}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
}

export default function ProviderDetailPage() {
    const { appKey, name } = useParams<{ appKey: string; name: string }>();
    const { data } = useCapabilityDetail(appKey!, name!);
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);

    const config = useMemo(() => resolveProviderConfig(data), [data]);
    const supportedModels = asStringList(
        config.supportedModels,
        config.supported_models,
        config.models,
    );
    const features = asStringList(config.features);

    const providerInfoSection = (supportedModels.length > 0 || features.length > 0) ? (
        <div className="mt-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-card p-6 text-card-foreground shadow-sm dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-900">Provider info</h2>
            <div className="flex flex-wrap gap-2">
                {supportedModels.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                        <Cpu size={12} />
                        {supportedModels.length} model{supportedModels.length !== 1 ? 's' : ''} supported
                    </span>
                )}
                {features.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700">
                        <Zap size={12} />
                        {features.length} feature{features.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
        </div>
    ) : null;

    return (
        <CapabilityDetailLayout
            typeLabel="Provider"
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
            overviewChildren={providerInfoSection}
        >
            <SupportedModelsTab
                supportedModels={supportedModels}
                features={features}
            />
        </CapabilityDetailLayout>
    );
}
